import { isDevice4GMeter } from './deviceMeter';
import { createMqttWS } from './mqttWS';

/** 与 index4g yjRemoteMqttRxWaitMsByMappingTier 一致 */
export const YJ_REMOTE_MQTT_WAIT_MS_TIER1 = 8000;
export const YJ_REMOTE_MQTT_WAIT_MS_TIER2 = 16000;

export function yjRemoteMqttRxWaitMsByMappingTier(mappingFanoutTier) {
  return Number(mappingFanoutTier) === 2 ? YJ_REMOTE_MQTT_WAIT_MS_TIER2 : YJ_REMOTE_MQTT_WAIT_MS_TIER1;
}

/** QK/NQK：表号参与指令拼接；4G/YJ 等优先用 MAC 查映射（与小程序 test1 一致） */
export function isQKOrNQKMeter(meterNo) {
  const m = String(meterNo || '').trim().toUpperCase();
  return m.startsWith('QK') || m.startsWith('NQK');
}

/** 下发 MQTT / publish-by-meter 时使用的 meter_no（与 device_topic_mapping 一致） */
export function getAdminMeterPublishKey(device) {
  if (!device) return '';
  const is4G = isDevice4GMeter(device);
  let mac = String(device.device_mac || '').trim();
  if (mac.includes(':')) mac = mac.replace(/:/g, '');
  const meterNo = String(device.current_meter_no || '').trim();
  const useMac = (is4G || !isQKOrNQKMeter(meterNo)) && mac;
  const key = useMac ? mac : meterNo;
  return key || String(device.factory_meter_no || '').trim();
}

function crc8(buffer) {
  let crc = 0x00;
  for (let i = 0; i < buffer.length; i++) {
    crc ^= buffer[i];
    for (let b = 0; b < 8; b++) {
      if (crc & 0x80) crc = (crc << 1) ^ 0x07;
      else crc <<= 1;
      crc &= 0xff;
    }
  }
  return crc & 0xff;
}

export function hexToBytes(hexStr) {
  const clean = String(hexStr || '').replace(/\s+/g, '').toLowerCase();
  if (clean.length % 2 !== 0) return null;
  const bytes = [];
  for (let i = 0; i < clean.length; i += 2) {
    const byte = parseInt(clean.substr(i, 2), 16);
    if (Number.isNaN(byte)) return null;
    bytes.push(byte);
  }
  return bytes;
}

export function buildFinalPayload(rawHex, autoCrc = true) {
  const clean = String(rawHex || '').replace(/\s+/g, '').toLowerCase();
  const bytes = hexToBytes(clean);
  if (!bytes) throw new Error('指令格式不合法（需偶数位16进制字符）');
  if (!autoCrc) return clean;
  const crc = crc8(bytes);
  const crcHex = crc.toString(16).padStart(2, '0');
  return clean + crcHex;
}

/** 与 test1：非 BLE 直连时 meterNo 须为 12 位十六进制表号地址 */
export function remotePayloadUseBleDirect(meterKey) {
  const m = String(meterKey || '').trim();
  return !/^[0-9a-fA-F]{12}$/i.test(m);
}

export function buildRemoteReadHex(meterNo, isBleDirect) {
  const m = String(meterNo || '').trim();
  if (!isBleDirect && !/^[0-9a-fA-F]{12}$/.test(m)) {
    throw new Error('表号不合法（非 BLE 直连时需 12 位十六进制地址）');
  }
  return ((isBleDirect ? '' : m) + '51' + '02').toLowerCase();
}

/** 写入剩余电量/水量（绝对值，单位：度或吨） */
export function buildRemoteSetPowerHex(meterNo, power, isBleDirect) {
  const m = String(meterNo || '').trim();
  if (!isBleDirect && !/^[0-9a-fA-F]{12}$/.test(m)) {
    throw new Error('表号不合法（非 BLE 直连时需 12 位十六进制地址）');
  }
  const v = Math.max(0, Number(power) || 0);
  const vInt = Math.round(v * 1000);
  const valueHex = vInt.toString(16).padStart(8, '0');
  return ((isBleDirect ? '' : m) + '45' + '02' + valueHex).toLowerCase();
}

/** 开闸/关闸（与 test4 buildSwitchCommandHex 一致，不含 CRC） */
export function buildRemoteSwitchHex(meterNo, action, isBleDirect) {
  const m = String(meterNo || '').trim();
  if (!isBleDirect && !/^[0-9a-fA-F]{12}$/.test(m)) {
    throw new Error('表号不合法（非 BLE 直连时需 12 位十六进制地址）');
  }
  const p3 = action === 'close' ? '02' : '01';
  return ((isBleDirect ? '' : m) + '57' + '02' + p3).toLowerCase();
}

/** 重启设备（与 test4 chongqi 一致，不含 CRC） */
export function buildRemoteRebootHex(meterNo, isBleDirect) {
  const m = String(meterNo || '').trim();
  if (!isBleDirect && !/^[0-9a-fA-F]{12}$/.test(m)) {
    throw new Error('表号不合法（非 BLE 直连时需 12 位十六进制地址）');
  }
  return ((isBleDirect ? '' : m) + '52' + '02').toLowerCase();
}

export function getRxAddressCandidates(device) {
  const set = new Set();
  const add = (raw) => {
    let v = String(raw || '').trim().toLowerCase().replace(/:/g, '');
    if (v.length >= 12 && /^[0-9a-f]{12}/.test(v)) set.add(v.slice(0, 12));
  };
  if (!device) return [];
  add(device.device_mac);
  add(device.current_meter_no);
  add(device.factory_meter_no);
  return Array.from(set);
}

/** 8 位 hex 作 32 位有符号大端整数（千分之一度），与小程序 yjMeterEnergyHex8ToSignedMilli 一致 */
function yjMeterEnergyHex8ToSignedMilli(hex8) {
  const h = String(hex8 || '').trim();
  if (h.length !== 8) return 0;
  const n = parseInt(h, 16);
  if (!Number.isFinite(n)) return 0;
  return n | 0;
}

/** 与 index4g _mqttRemainTotalLookSane */
export function mqttRemainTotalLookSane(remain, total) {
  const r = Number(remain);
  const t = Number(total);
  const maxKwh = 200000;
  if (!Number.isFinite(r) || !Number.isFinite(t)) return false;
  if (r < -1 || t < -1) return false;
  if (r > maxKwh || t > maxKwh) return false;
  return true;
}

/** 从 51/57/54 原始回包解析开关闸（01=通电，02=断电） */
export function parseSwitchStatusFromRaw(rawMsg) {
  const msg = String(rawMsg || '').trim().toLowerCase().replace(/\s+/g, '');
  if (msg.length < 18) return null;
  const parseEmsw = (hex2) => {
    const n = parseInt(hex2, 16);
    if (n === 1) return '通电';
    if (n === 2) return '断电';
    return null;
  };
  if (msg.substring(12, 14) === '57') return parseEmsw(msg.substring(16, 18));
  if (msg.length >= 60 && msg.substring(12, 14) === '51') return parseEmsw(msg.substring(58, 60));
  if (msg.substring(12, 14) === '54' && msg.length >= 76) return parseEmsw(msg.slice(-6, -4));
  return null;
}

/** 解析 MQTT 下行 51 抄表回调（与 index4g parseRxTopicMessage 一致） */
export function parseMqtt51ReadResponse(rawMsg, addressCandidates) {
  const msg = String(rawMsg || '').trim().toLowerCase().replace(/\s+/g, '');
  if (msg.length < 34) return null;
  const callbackMeterNo = msg.substring(0, 12);
  const candidates = addressCandidates || [];
  if (candidates.length > 0 && !candidates.includes(callbackMeterNo)) return null;
  if (msg.substring(12, 14) !== '51') return null;
  const totalRaw = yjMeterEnergyHex8ToSignedMilli(msg.substring(18, 26));
  const remainRaw = yjMeterEnergyHex8ToSignedMilli(msg.substring(26, 34));
  const remain = remainRaw / 1000;
  const total = totalRaw / 1000;
  if (!mqttRemainTotalLookSane(remain, total)) return null;
  const switchStatus = parseSwitchStatusFromRaw(msg) || '';
  return { remain, total, switchStatus };
}

/** 档 1：仅 primary rx；档 2：primary + candidates 去重（与 index4g _yjRemoteMqttRxTopicsForHttpPoll） */
export function rxTopicsForHttpPoll(mapData) {
  const primary = String(mapData?.rx_topic || '').trim();
  if (!primary) return [];
  if (Number(mapData?.mapping_fanout_tier) !== 2) return [primary];
  const seen = new Set([primary]);
  const out = [primary];
  const cand = mapData?.candidates || [];
  for (let i = 0; i < cand.length; i++) {
    const rx = String(cand[i]?.rx_topic || '').trim();
    if (!rx || seen.has(rx)) continue;
    seen.add(rx);
    out.push(rx);
  }
  return out;
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function shouldAcceptPolledRaw({ raw, lastRaw, publishAt, msgRes }) {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return { accept: false, trimmed: '' };
  if (lastRaw && trimmed === lastRaw) {
    const ua = msgRes?.data?.updated_at;
    if (ua && publishAt) {
      const ut = Date.parse(ua);
      if (!Number.isNaN(ut) && ut >= publishAt - 500) return { accept: true, trimmed };
    }
    return { accept: false, trimmed };
  }
  return { accept: true, trimmed };
}

/**
 * 管理端远程 51 抄表（对齐 index4g remoteReadMeter：QoS1、档位超时、WS+HTTP、多 RX、sent_at 去重）
 * @returns {Promise<{ remain: number, total: number } | null>}
 */
export async function adminRemoteRead51({
  device,
  getMqttMappingByMeter,
  mqttPublishByMeter,
  getMqttLatestMessage,
  useWebSocket = true,
}) {
  const publishKey = getAdminMeterPublishKey(device);
  if (!publishKey) throw new Error('无法确定表号或 MAC，请检查设备档案');

  const mapRes = await getMqttMappingByMeter(publishKey);
  if (!mapRes?.success || !mapRes?.data?.rx_topic) {
    throw new Error(mapRes?.msg || mapRes?.error || '未找到该设备的 MQTT 主题映射');
  }
  const mapData = mapRes.data;
  const tier = Number(mapData.mapping_fanout_tier) === 2 ? 2 : 1;
  const waitMs = yjRemoteMqttRxWaitMsByMappingTier(tier);
  const rxTopics = rxTopicsForHttpPoll(mapData);
  const addressCandidates = getRxAddressCandidates(device);
  const isBleDirect = remotePayloadUseBleDirect(publishKey);
  const payload = buildFinalPayload(buildRemoteReadHex(publishKey, isBleDirect), true);
  const publishAt = Date.now();
  const pollOpts = buildMqttMessagePollOpts(publishKey, 'read', publishAt);

  const tryPublish = async (mk) => {
    const pubRes = await mqttPublishByMeter({ meter_no: mk, payload, qos: 1 });
    if (!pubRes?.success) {
      throw new Error(pubRes?.msg || pubRes?.error || '抄表指令发送失败');
    }
  };

  try {
    await tryPublish(publishKey);
  } catch (e) {
    const msg = String(e?.message || e);
    const alt = String(device?.current_meter_no || device?.factory_meter_no || '').trim();
    if (/未找到该表号/.test(msg) && alt && alt.toLowerCase() !== String(publishKey).trim().toLowerCase()) {
      await tryPublish(alt);
    } else {
      throw e;
    }
  }

  let parsedFromWs = null;
  let wsHandle = null;
  const tryParse = (raw) => parseMqtt51ReadResponse(raw, addressCandidates);

  if (useWebSocket && mapData.rx_topic) {
    const extraRx = tier === 2 ? rxTopics.filter((t) => t !== mapData.rx_topic) : [];
    wsHandle = createMqttWS(
      mapData.rx_topic,
      (_topic, msg, hex) => {
        const raw = String(hex || msg || '').trim();
        const p = tryParse(raw);
        if (p) parsedFromWs = p;
      },
      { extraRxTopics: extraRx }
    );
    try {
      await Promise.race([wsHandle.ready, sleep(3000)]);
    } catch (_) {
      wsHandle = null;
    }
  }

  const deadline = Date.now() + waitMs;
  let lastRaw = '';
  while (Date.now() < deadline) {
    if (parsedFromWs) {
      wsHandle?.close();
      return parsedFromWs;
    }

    const pairs = await Promise.all(
      rxTopics.map(async (topic) => {
        try {
          const msgRes = await getMqttLatestMessage(topic, 'raw', pollOpts);
          return { msgRes };
        } catch {
          return { msgRes: null };
        }
      })
    );

    for (let i = 0; i < pairs.length; i++) {
      const { msgRes } = pairs[i];
      const raw = msgRes?.data?.message ?? msgRes?.data?.hex ?? '';
      const { accept, trimmed } = shouldAcceptPolledRaw({ raw, lastRaw, publishAt, msgRes });
      if (!accept) continue;
      lastRaw = trimmed;
      const p = tryParse(trimmed);
      if (p) {
        wsHandle?.close();
        return p;
      }
    }
    await sleep(400);
  }

  wsHandle?.close();
  return null;
}

/** 与小程序 GET /api/mqtt/message 一致：meter_no、op、sent_at；多 rx 聚合由服务端按 device_info 档位解析，不必传 mapping_fanout_tier */
export function buildMqttMessagePollOpts(publishKey, op, sentAtMs) {
  const mk = String(publishKey || '').trim().toLowerCase().replace(/:/g, '');
  const opts = { op: op || '' };
  if (/^[0-9a-f]{12}$/.test(mk)) opts.meter_no = mk;
  const n = Number(sentAtMs);
  if (sentAtMs != null && Number.isFinite(n)) opts.sent_at = String(Math.floor(n));
  return opts;
}
